class Searchbox {
  selectedIndex = -1;
  sbShowSuggestions = 'show-suggestions';
  sbShow = 'show';

  constructor(searchboxElement) {
    this.searchbox = searchboxElement;
    this.form = this.searchbox.querySelector('.sb__form')
    this.input = this.searchbox.querySelector('.sb__input');
    this.suggestionsList = this.searchbox.querySelector('.sb__suggestions-list');
    this.submit = this.searchbox.querySelector('.sb__submit');
    this.clearButton = this.searchbox.querySelector('.sb__clear');
  }

  /**
   * Initializes the searchbox.
   * 
   * @param {callback} callback method which returns the suggestions in an array of list items (li).
   */
  init(callback) {
    this.suggestionsCallback = callback;
    this.addEvents();
  }

  /**
   *  Adds event listeners for typing input, selecting items and the clear button.
   */
  addEvents() {
    this.searchbox.addEventListener('keyup', e => {
      switch (e.key) {
        case 'ArrowUp':
          if (this.selectedIndex > -1) this.selectedIndex--;
          this.setSelected(this.listItems, true);
          break;
        case 'ArrowDown':
          if (this.listItems.length-1 > this.selectedIndex) this.selectedIndex++;
          this.setSelected(this.listItems, true);
          break;
        default:
          this.selectedIndex = -1;
          if (this.input.value.length > 0) {
            this.suggestionsCallback(this.input.value).then((response) => {
              this.showSuggestions(response);
            });
          }
          this.setElements();
      }
    });

    this.clearButton.addEventListener('click', () => { this.clear() });
    
    this.suggestionsList.addEventListener('click', e => {
      this.input.value = e.target.tagName !== 'LI' ? e.target.closest('li').outerText : e.target.outerText;
      this.submit.click();
    });

    this.suggestionsList.addEventListener('mouseover', e => {
      if (e.target.tagName !== 'LI') return;

      this.selectedIndex = Array.prototype.indexOf.call(this.listItems, e.target);
      this.setSelected(this.listItems, false);
    });    
  }

  /**
   * Adds the selected class to the selected item and adds the value to the input field.
   * 
   * @param {Array} list array with all suggestions as list items (li)
   * @param {boolean} updateInput update input field with selected value
   */
  setSelected(list, updateInput) {
    list.forEach(li => {
      li.classList.remove('selected');
      li.ariaSelected = 'false';
    });

    if (this.selectedIndex > -1) {
      list[this.selectedIndex].classList.add('selected');
      list[this.selectedIndex].ariaSelected = 'true';
      if (updateInput) this.input.value = list[this.selectedIndex].innerText;
    }
  }

  /**
   * Shows the suggestions that are supplied with the listitems parameter and sets attributes.
   * @param {string} listitems string with all suggestions as list items (li)
   */
  async showSuggestions(listitems) {
    // If listitems is undefined, do nothing. Empty string or with listitems will update the suggestions.
    if (listitems === undefined) return;

    this.suggestionsList.innerHTML = listitems;
    this.listItems = this.suggestionsList.childNodes;

    this.listItems.forEach((li) => { 
      li.ariaLabel = li.innerText;
      li.ariaAtomic = 'true';
      li.setAttribute('role', 'option');
    });

    this.searchbox.querySelector('.sb__suggestions-count').innerHTML = `${this.listItems.length} zoeksuggesties beschikbaar`;

    if (listitems?.length > 0) {
      this.searchbox.classList.add(this.sbShowSuggestions);
      this.input.setAttribute('aria-expanded', 'true');
    } else {
      this.searchbox.classList.remove(this.sbShowSuggestions);
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Changes elements when there is user input
   */
  setElements() {
    if (this.input.value.length > 0) {
      this.clearButton.classList.add(this.sbShow);
      this.submit.removeAttribute('disabled');
    } else {
      this.clearButton.classList.remove(this.sbShow);
      this.submit.setAttribute('disabled', '');
      this.searchbox.classList.remove(this.sbShowSuggestions);
      this.searchbox.querySelector('.sb__suggestions-count').innerHTML = '';
      while (this.suggestionsList.firstChild) {
        this.suggestionsList.removeChild(this.suggestionsList.firstChild);
      }
    }
  }

  /**
   * Clears input field, removes clear button and suggestions.
   */
  clear() {
    this.input.value = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.setElements();
    this.input.focus();
  } 
}

class CoveoSearchbox extends Searchbox {
  apiUrl = `https://${this.searchbox.dataset.organization}.org.coveo.com/rest/search/v2/querySuggest?organizationId=${this.searchbox.dataset.organization}`
  apiKey = this.searchbox.dataset.key;
  searchHub = this.searchbox.dataset.searchhub;
  coveoHistoryKey = '__coveo.analytics.history';
  coveoVisitorIdKey = 'visitorId';
  prevController = new AbortController();

  constructor(searchboxElement) {
    super(searchboxElement);
    this.init(this.getSuggestions);
    this.addCoveoSearchboxEvents();
  }

  /**
   * Adds events for the Coveo searchbox.
   */
  addCoveoSearchboxEvents() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      window.location.href = `${e.target.action}${encodeURIComponent(this.input.value)}`;
    });
  }

  /**
   * Gets the suggestions from the Coveo rest API and returns an array of list items.
   * 
   * @param {string} inputValue input value
   * @returns a string with list items (li)
   */
  async getSuggestions(inputValue) {
    // Cancel previous request if pending, except by the first characters in the input field (to show some results while typing in case of a slow connection)
    if (this.prevController.current && this.input.value.length > 2) {
      this.prevController.current.abort();
    }
    
    // Create new abort controller
    const controller = new AbortController();
    this.prevController.current = controller;

    let data;
    try {
      // Fetch data from Coveo rest API
      const response = await fetch(this.apiUrl, { 
        method: 'POST',
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${this.apiKey}`
        },
        signal: controller.signal,
        body: this.getSuggestionParameters(inputValue)
      });
      data = await response.json();
    } catch { return; };

    // Create list items
    let list = '';
    data?.completions?.forEach(function(item) {
      list += `<li>${ item.highlighted.replace(/[{}]/g, '').replace(/[(\[]/g, '<b>').replace(/[)\]]/g, '</b>') }</li>`;
    });

    return list;
  }

  /**
   * Gets data from local storage, gets tab information and builds a parameters string for the Coveo suggestions API
   * @returns {URLSearchParams} with the parameters
   */
  getSuggestionParameters(inputValue) {
    // Get data from local storage
    const actionsHistory = localStorage.getItem(this.coveoHistoryKey);
    const visitorId = localStorage.getItem(this.coveoVisitorIdKey);

    // Get search tab
    const actionParams = this.form?.action?.split('#')[1];
    const tab = new URLSearchParams(actionParams)?.get('tab');

    // Add data to parameters string
    const params = new URLSearchParams();
    params.append('locale', 'nl');
    params.append('count', 5);
    params.append('q', inputValue);
    params.append('enableWordCompletion', 'true');
    params.append('searchHub', this.searchHub);
    if (tab) params.append('tab', tab);
    if (actionsHistory) params.append('actionsHistory', actionsHistory);
    if (visitorId) params.append('visitorId', visitorId);
    
    return params;
  }
}

class Searchbar extends CoveoSearchbox {
  sbarShow = 'sbar--show';

  constructor(searchboxElement) {
    super(searchboxElement);
    this.searchbar = this.searchbox.closest('.sbar');
    this.searchbarButton = document.querySelector('.nav__search');
    this.addSearchbarEvents();
  }

  /**
   * Adds events for the search bar.
   */
  addSearchbarEvents() {
    document.querySelectorAll('.sbar__close, .nav__search, .sbar__overlay').forEach(e => {
      e.addEventListener('click', () => this.toggleSearchbar())
    });

    this.searchbar.addEventListener('keyup', e => {
      if (e.key == 'Escape') {
        this.toggleSearchbar();
      }
    });

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.toggleSearchbar();
    });
  }

  /**
   * Toggles the searchbar
   */
  toggleSearchbar() {
    this.clear();
    this.searchbar.classList.toggle(this.sbarShow);

    if (this.searchbar.classList.contains(this.sbarShow)) {
      setTimeout(() => this.input.focus(), 250);
      this.searchbarButton.ariaExpanded = 'true';
    }
    else {
      setTimeout(() => this.searchbarButton.focus(), 250);
      this.searchbarButton.ariaExpanded = 'false';
    }
  }  
}

window.searchbox = (function() {
  'use strict';

  var searchbar = document.querySelector('.sb.sb--bar');
  if (searchbar) new Searchbar(searchbar);

  var coveoSearchboxes = document.querySelectorAll('.sb.sb--coveo');
  coveoSearchboxes.forEach((sb) => {
    if (sb) new CoveoSearchbox(sb);
  });
})();